import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Pages
import LandingPage from "@/pages/LandingPage";
import ShopPage from "@/pages/ShopPage";
import TeaDetailPage from "@/pages/TeaDetailPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import BlendBuilderPage from "@/pages/BlendBuilderPage";
import BodyBlendBuilderPage from "@/pages/BodyBlendBuilderPage";
import ProductsPage from "@/pages/ProductsPage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import WhippedButterPage from "@/pages/WhippedButterPage";
import BodyWashPage from "@/pages/BodyWashPage";
import CartPage from "@/pages/CartPage";
import CheckoutSuccessPage from "@/pages/CheckoutSuccessPage";
import MyAccountPage from "@/pages/MyAccountPage";
import HistoryPage from "@/pages/HistoryPage";
import WhoWeArePage from "@/pages/WhoWeArePage";
import AuthPage from "@/pages/AuthPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cartCount, setCartCount] = useState(0);

  useEffect(() => {
    checkAuth();
    // Listen for guest cart updates
    const handleGuestCartUpdate = () => {
      if (!user) {
        updateGuestCartCount();
      }
    };
    window.addEventListener('guestCartUpdated', handleGuestCartUpdate);
    return () => window.removeEventListener('guestCartUpdated', handleGuestCartUpdate);
  }, [user]);

  const updateGuestCartCount = () => {
    const guestCart = JSON.parse(localStorage.getItem('guestCart') || '[]');
    setCartCount(guestCart.length);
  };

  const checkAuth = async () => {
    try {
      // Check for session_id in URL fragment
      const hash = window.location.hash;
      if (hash && hash.includes('session_id=')) {
        const sessionId = hash.split('session_id=')[1].split('&')[0];
        await processSessionId(sessionId);
        // Clean URL
        window.history.replaceState(null, '', window.location.pathname);
        return;
      }

      // Check existing session
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      await fetchCartCount();
    } catch (error) {
      console.log('Not authenticated');
      // Load guest cart count
      updateGuestCartCount();
    } finally {
      setLoading(false);
    }
  };

  const processSessionId = async (sessionId) => {
    try {
      const response = await axios.post(
        `${API}/auth/session`,
        {},
        {
          headers: { 'X-Session-ID': sessionId },
          withCredentials: true
        }
      );
      setUser(response.data.user);
      toast.success('Welcome back!');
      await fetchCartCount();
    } catch (error) {
      console.error('Session processing error:', error);
      toast.error('Authentication failed');
    }
  };

  const fetchCartCount = async () => {
    try {
      const response = await axios.get(`${API}/cart`, { withCredentials: true });
      setCartCount(response.data.items?.length || 0);
    } catch (error) {
      console.error('Cart fetch error:', error);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      setCartCount(0);
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sage-600"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, setUser, logout, cartCount, fetchCartCount }}>
      <BrowserRouter>
        <div className="App">
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/shop" element={<ShopPage />} />
            <Route path="/tea/:id" element={<TeaDetailPage />} />
            <Route path="/product/:productId" element={<ProductDetailPage />} />
            <Route path="/blend" element={<BlendBuilderPage />} />
            <Route path="/body-blend" element={<BodyBlendBuilderPage />} />
            <Route path="/products" element={<ProductsPage />} />
            <Route path="/whipped-butter" element={<WhippedButterPage />} />
            <Route path="/body-wash" element={<BodyWashPage />} />
            <Route path="/subscriptions" element={<SubscriptionPage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/checkout-success" element={<CheckoutSuccessPage />} />
            <Route path="/my-account" element={user ? <MyAccountPage /> : <Navigate to="/auth" />} />
            <Route path="/who-we-are" element={<WhoWeArePage />} />
            <Route path="/history" element={<HistoryPage />} />
            <Route path="/auth" element={<AuthPage />} />
          </Routes>
          <Toaster position="top-center" richColors />
        </div>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;
